import { coverageTone, type PipelineNeed, rateNote, type WinHistory } from '@crm/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { compactSar } from './AttentionPeek';

/** Next year for one person (Home) — the server's NextYearRow, with the year. */
export interface NextYearFigures { year: number; pipeline: number; quoted: number; likely: number; deals: number; target: number | null; need: PipelineNeed }
interface Person extends Omit<NextYearFigures, 'year'> { userId: string; name: string }
interface Company { pipeline: number; quoted: number; likely: number; deals: number; target: number | null; neededYear: number | null; neededNow: number | null; coverage: number | null; history: WinHistory }

const pct = (n: number) => `${Math.round(n * 100)}%`;
const dayMonth = (ymd: string) => new Date(`${ymd}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
/** "22% · 9 of 31 · 75 days to PO", from the person's own record (or the company's when theirs is too thin). */
const rateText = (n: PipelineNeed) => n.rate == null ? '—'
  : `${pct(n.rate)}${n.source === 'own' ? ` · ${n.history.won} of ${n.history.closed}` : ''}${n.cycleDays != null ? ` · ${Math.round(n.cycleDays)} days to PO` : ''}`;

/** Quoted against what's needed today: the two figures, the bar, and the lines under them. */
function NeedFigures({ year, quoted, pipeline, deals, target, neededNow, neededYear, coverage, reached, lines }: {
  year: number; quoted: number; pipeline: number; deals: number; target: number | null; neededNow: number | null; neededYear: number | null; coverage: number | null; reached: boolean; lines: ReactNode;
}) {
  const tone = coverageTone(coverage);
  return (
    <>
      <div className="ny-row">
        <Link to="/o/opportunity?mine=true" className="ny-fig" title={`Open deals with a quotation sent and the PO expected in ${year} (the PO date, else the deal engine's, else the close date). Only these count as quoted.`}>
          <b>{compactSar(quoted)} quoted</b>
          <span>of {compactSar(pipeline)} pipeline · {deals} deal{deals === 1 ? '' : 's'}{pipeline - quoted > 0 ? ` · ${compactSar(pipeline - quoted)} not quoted yet` : ''}</span></Link>
        <div className={`ny-cover ${tone}`}>
          {!target ? <><b>—</b><span>no {year} target set yet</span></>
            : reached ? <><b>✓</b><span>the {compactSar(target)} target is already won</span></>
            : neededNow == null ? <><b>—</b><span>pipeline needed can’t be worked out yet</span></>
            : <><b>{pct(coverage ?? 0)}</b><span>of the {compactSar(neededNow)} needed quoted now{neededYear != null && neededYear !== neededNow ? ` (${compactSar(neededYear)} over the year)` : ` · ${compactSar(target)} target`}</span></>}
        </div>
      </div>
      {coverage != null && <i className={`ny-bar ${tone}`} aria-hidden="true"><u style={{ width: `${Math.min(100, coverage * 100)}%` }} /></i>}
      {lines}
    </>
  );
}

/**
 * Home yearly target, from October: next year's quoted pipeline against what the target needs quoted, worked out from
 * the person's own win rate by value and time to PO (else the company's, flagged). Quote-by dates are estimates.
 */
export function NextYearStrip({ ny }: { ny: NextYearFigures }) {
  const n = ny.need;
  const note = rateNote(n);
  return (
    <div className="ny-strip" aria-label={`${ny.year} pipeline`}>
      <div className="tg-eye"><span>{ny.year} pipeline</span><span>needed = target ÷ win rate</span></div>
      <NeedFigures year={ny.year} quoted={ny.quoted} pipeline={ny.pipeline} deals={ny.deals} target={ny.target} neededNow={n.neededNow} neededYear={n.neededYear} coverage={n.coverage} reached={n.reached}
        lines={ny.target && !n.reached ? (
          <p className="ny-lines small">
            {n.rate != null && <span>Win rate {rateText(n)}{n.neededYear != null && <> · <b>{compactSar(n.neededYear)}</b> to quote over {ny.year}</>}</span>}
            {note && <span className="ny-flag">{note}</span>}
            {n.rate != null && n.cycleDays == null && <span title="Fewer than 5 wins (theirs, and the company's) had the quotation sent before the PO">Quote-by dates: not enough history yet</span>}
            {n.next && <span>Next: about <b>{compactSar(n.next.amount)}</b> quoted by <b>{dayMonth(n.next.by)}</b> for Q{n.next.quarter} wins <i>(estimate)</i>{n.lateQuarters > 0 && <span className="ny-late"> · {n.lateQuarters} earlier quarter{n.lateQuarters === 1 ? '' : 's'} late</span>}</span>}
          </p>
        ) : null} />
    </div>
  );
}

/** CEO page, from October: the company's quoted pipeline against what next year's targets need, and one row a person. */
export function NextYearPanel() {
  const q = useQuery({ queryKey: ['ceo-next-year'], queryFn: () => api<{ year: number; show: boolean; people: Person[]; company: Company | null }>('/api/ceo/next-year'), retry: false });
  const v = q.data;
  if (!v?.show || !v.company) return null;
  const c = v.company;
  const noTarget = v.people.filter((p) => p.target == null);
  const flagged = v.people.filter((p) => p.target && rateNote(p.need));
  return (
    <section className="panel ny-panel">
      <header className="panel-head"><h3>{v.year} pipeline vs what the targets need</h3>
        <span className="muted small">quoted deals with the PO expected in {v.year} · needed = target ÷ each person’s win rate</span></header>
      <div className="pad stack">
        <div className="ny-strip">
          <NeedFigures year={v.year} quoted={c.quoted} pipeline={c.pipeline} deals={c.deals} target={c.target} neededNow={c.neededNow} neededYear={c.neededYear} coverage={c.coverage} reached={false} lines={null} />
        </div>
        <div className="table-wrap flat"><table className="ny-table">
          <thead><tr><th>Salesperson</th><th className="num">Quoted</th><th className="num">Win rate</th><th className="num">{v.year} target</th>
            <th className="num" title={`Quotation value to send over ${v.year} (target ÷ win rate), and the open quoted pipeline needed today`}>To quote</th><th className="num">Coverage</th><th>Quote by (est.)</th></tr></thead>
          <tbody>{v.people.map((p) => {
            const n = p.need;
            return (
              <tr key={p.userId}>
                <td>{p.name}</td>
                <td className="num">{compactSar(p.quoted)}<div className="small muted">of {compactSar(p.pipeline)}</div></td>
                <td className="num">{n.rate != null ? pct(n.rate) : '—'}
                  <div className={`small ${n.source === 'own' ? 'muted' : 'ny-flag'}`} title={rateNote(n) ?? ''}>{n.source === 'own' ? `${n.history.won} of ${n.history.closed}` : n.source === 'company' ? 'company rate' : 'too little history'}{n.cycleDays != null ? ` · ${Math.round(n.cycleDays)} d to PO` : ''}</div></td>
                <td className="num">{p.target ? compactSar(p.target) : <span className="muted">not set</span>}</td>
                <td className="num">{n.neededYear != null ? compactSar(n.neededYear) : '—'}<div className="small muted">{n.reached ? 'target won' : n.neededNow != null ? `now ${compactSar(n.neededNow)}` : ''}</div></td>
                <td className={`num ny-c ${coverageTone(n.coverage)}`}><b>{n.coverage != null ? pct(n.coverage) : '—'}</b></td>
                <td className="small">{n.next ? <>{compactSar(n.next.amount)} by {dayMonth(n.next.by)} <span className="muted">Q{n.next.quarter}</span></>
                  : <span className="muted">{p.target && n.rate != null && n.cycleDays == null ? 'not enough history' : '—'}</span>}
                  {n.lateQuarters > 0 && <span className="ny-late"> · {n.lateQuarters} late</span>}</td>
              </tr>
            );
          })}</tbody>
        </table></div>
        <p className="small muted ny-notes">
          Only deals with a quotation sent count as quoted. Win rate = won value ÷ won and lost value of the quotations that closed in the last 12 months
          (company: {c.history.closed} closed, {c.history.closed ? pct(c.history.wonValue / Math.max(1, c.history.wonValue + c.history.lostValue)) : '—'} by value); anyone with fewer than 5, or no win, uses the company’s rate.
          Quote-by = the quarter’s end minus the median days from quotation to PO (only wins quoted before their PO, at least 5): an estimate.
          {flagged.length > 0 && <> Company rate used for {flagged.map((p) => p.name).join(', ')}.</>}
          {noTarget.length > 0 && <> No {v.year} target yet for {noTarget.map((p) => p.name).join(', ')}: plan {noTarget.length === 1 ? 'it' : 'them'} on <Link to="/forecasts">Forecasts → Plan {v.year}</Link>.</>}
        </p>
      </div>
    </section>
  );
}
